/* eslint-disable react/require-default-props */
import Close from '@material-ui/icons/Close'
import { useState } from 'react'
import { Text, Button } from '@resiliatech/birdsong'
import {
  Overlay,
  IconButton,
  ModalContainer,
  ModalBody,
  StepHeader,
  ItemWrapper,
  Index,
  StepActions,
  PrimaryButton,
} from './Wizard.styles'

type Action = {
  // The label to display for the 'next' button for current step
  name: string
  // A function to call when the 'next' button is clicked for current step
  handler?: () => void
  // Whether current step is required to be completed before moving to next step
  isRequired?: boolean
  // Whether current step is completed
  isCompleted?: boolean
  // The id of the form that links a form to the 'next' button (submit) for current step
  formId?: string

  disabled?: boolean
}

export type WizardStep = {
  // The step name to display in the header
  name: string
  // The component to display for current step
  component: React.ReactNode
  // The action to perform when the 'next' button is clicked for current step
  action: Action
  // Whether to hide the name text
  hideName: boolean
}

type WizardProps = {
  // Whether the wizard is open
  isOpen: boolean
  // A function to call when the wizard is closed
  onToggle: (isOpen: boolean) => void
  onCancel: (step: number) => void
  // The steps to display in the wizard
  steps: WizardStep[]
  // The width of the wizard, if not provided defaults to 50%
  width?: string
  // The height of the wizard, if not provided defaults to auto
  height?: string
}

const Wizard = ({
  isOpen,
  onToggle,
  onCancel,
  steps,
  width,
  height,
}: WizardProps) => {
  const [currentStep, setCurrentStep] = useState(0)
  const {
    action: {
      name: actionName,
      handler: actionHandler,
      isRequired: isActionRequired,
      isCompleted: isActionCompleted,
      formId,
      disabled,
    },
    component,
  } = steps[currentStep]

  const onCancelHandler = () => {
    onCancel(currentStep)
  }

  const onBack = () => setCurrentStep((current) => current - 1)

  const onNext = () => {
    if (actionHandler) actionHandler()
    // If action is required and not completed, do not move to next step
    if (isActionRequired && !isActionCompleted) return
    // If action is not required or already completed, move to next step
    if (currentStep < steps.length - 1) setCurrentStep((current) => current + 1)
    else onToggle(false)
  }

  return isOpen ? (
    <>
      <Overlay onClick={onCancelHandler} />
      <ModalContainer width={width} height={height}>
        <IconButton onClick={onCancelHandler}>
          <Close />
        </IconButton>
        <ModalBody>
          <StepHeader>
            {steps.map(({ name: stepName, hideName }, index) => (
              <ItemWrapper key={stepName}>
                <Index active={index === currentStep}>
                  <Text variant='h3'>{index + 1}</Text>
                </Index>
                {!hideName && <Text variant='p1'>{stepName}</Text>}
              </ItemWrapper>
            ))}
          </StepHeader>
          {component}
          <StepActions>
            {currentStep > 0 && currentStep <= steps.length - 1 && (
              <Button variant='tertiary' label='Back' onClick={onBack} />
            )}
            {currentStep <= steps.length - 1 && (
              <PrimaryButton
                form={formId}
                type='submit'
                onClick={onNext}
                disabled={disabled}
              >
                {actionName}
              </PrimaryButton>
            )}
          </StepActions>
        </ModalBody>
      </ModalContainer>
    </>
  ) : null
}

export default Wizard
